import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PolForumlaExprDialog } from '../../polFormula/PolFormulaExprDialog';

export const PolFormulaCompare = () => {
  const { navState } = useNavState();
  const { hasRole } = usePrincipal();
  const { openDialog } = useDialog();

  const rsrc = useRsrc();
  const entityListRef = useRef();

  const [pols, setPols] = useState([]);
  const [isOutline, setIsOutline] = useState(false);

  const polIds = useMemo(() => {
    if (Array.isArray(navState?.polIds) && navState.polIds.length > 0) {
      return navState.polIds;
    }
    if (Array.isArray(navState?.pols) && navState.pols.length > 0) {
      return navState.pols.map((pol) => pol.id).filter(Boolean);
    }
    return [];
  }, [navState?.polIds, navState?.pols]);

  const polKey = useMemo(() => polIds.join('|'), [polIds]);

  useEffect(() => {
    if (polIds.length !== 2) {
      setPols([]);
      return;
    }

    const rowsReq = { expr: ExprUtils.whereParams('id', polIds) };
    rsrc.rows('gp', 'POL', rowsReq).then((polRows) => {
      const sortedPols = new Array(polIds.length);
      polRows.forEach((pol) => {
        const index = polIds.indexOf(pol.id);
        if (index >= 0) {
          sortedPols[index] = pol;
        }
      });
      setPols(sortedPols.filter(Boolean));
    });
  }, [polKey]);

  const augFieldDefs = (fieldToFieldDef) => {
    if (pols.length !== 2) {
      return;
    }

    const typeFieldDef = fieldToFieldDef.type;

    ObjUtils.clearObj(fieldToFieldDef);

    const [leftPol, rightPol] = pols;
    fieldToFieldDef.name = { field: 'name' };
    fieldToFieldDef.code = { field: 'code' };
    fieldToFieldDef.type = typeFieldDef;

    fieldToFieldDef.pol0 = {
      field: 'pol0',
      displayName: leftPol?.name ?? 'Policy A',
      children: [
        {
          field: 'value0',
          displayName: 'Value',
          valueGetter: (params) => params.data?.value0,
        },
      ],
    };

    fieldToFieldDef.pol1 = {
      field: 'pol1',
      displayName: rightPol?.name ?? 'Policy B',
      children: [
        {
          field: 'value1',
          displayName: 'Value',
          valueGetter: (params) => params.data?.value1,
        },
      ],
    };

    fieldToFieldDef.match = {
      field: 'match',
      width: 70,
      cellClass: 'ag-center-cell',
      type: 'MATCH',
    };

    // Outline logic: group by code
    if (isOutline) {
      fieldToFieldDef.code = {
        field: 'code',
        displayName: 'Code',
        autoGrouping: true,
        rowGroup: true,
      };
    }
  };

  // Flat View
  const augRowsReq = useCallback(
    (rowsReq) => {
      rowsReq.query = 'POL_FORMULA_COMPARE';
      rowsReq.replaceParams = { leftPolId: polIds[0], rightPolId: polIds[1] };
      rowsReq.sortModel = [
        { colId: 'left_pol_formula_id', sort: 'asc' },
        { colId: 'right_pol_formula_id', sort: 'asc' },
      ];
    },
    [polIds],
  );

  // Outline View
  const clientSideRowData = useCallback((setDataSource) => {
    const reqParam = {
      polId: polIds[0],
      polIds: polIds,
    };
    rsrc.data('gp', 'POL_FORMULA_DATA_PLUGIN', reqParam).then((res) => {
      setDataSource(res);
    });
  }, []);

  const openPolFormulaExprDialog = (rowData) => {
    const formulaMatch = rowData.matchRes.includes('"MATCH"');
    const data = {
      name: rowData.name,
      code: rowData.code,
      polId: formulaMatch ? polIds[0] : polIds,
      module: 'gp',
    };
    openDialog(PolForumlaExprDialog, { data });
  };

  const getContextMenuItems = (params) => {
    let contextMenuItems = [];

    const data = params.node.data;
    const formulaName = 'Formula Expression';
    const polFormulaApplicable = !['FACTOR', 'REF', 'INFO'].includes(data.type);
    const hasPolFormulaView = hasRole('GP_POL_FORMULA_VIEW');
    if (polFormulaApplicable && hasPolFormulaView) {
      contextMenuItems.push({
        name: formulaName,
        icon: fas.faInfoCircle,
        action: () => openPolFormulaExprDialog(data),
      });
    }
    return contextMenuItems;
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%' }}
    >
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Title icon={fas.faBalanceScale} title="Policy Formula Compare" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={isOutline ? 'outline' : 'flat'}
            exclusive
            onChange={(e, value) => {
              setIsOutline(value === 'outline');
            }}
            sx={{ height: 40, color: 'primary.main' }}
          >
            <ToggleButton value="outline" sx={{ whiteSpace: 'nowrap' }}>
              Outline
            </ToggleButton>
            <ToggleButton value="flat" sx={{ whiteSpace: 'nowrap' }}>
              Flat
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <EntityList
        key={isOutline + '-' + pols?.length}
        ref={entityListRef}
        module="gp"
        entity="POL_FORMULA"
        showTitle={false}
        augRowsReq={isOutline ? null : augRowsReq}
        augFieldDefs={augFieldDefs}
        clientSideRowData={isOutline ? clientSideRowData : null}
        gridOptions={{
          treeData: isOutline,
          defaultExpandLevel: 0,
          getContextMenuItems,
        }}
      />
    </Box>
  );
};
